"use client";

import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DiffEditor, Editor } from "@monaco-editor/react";
import { Reorder } from "framer-motion";
import { PlusIcon } from "lucide-react";

import { cn } from "@acme/ui";
import { Button } from "@acme/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@acme/ui/context-menu";
import { Label } from "@acme/ui/label";
import { ScrollArea, ScrollBar } from "@acme/ui/scroll-area";
import { Switch } from "@acme/ui/switch";
import { Tabs, TabsContent, TabsList } from "@acme/ui/tabs";

import type { RemoteRunFileArea } from "../_hooks/use-monaco-editor";
import { umami } from "~/utils/analytics";
import {
  useAstGrepRemoteRunFiles,
  useAstGrepRemoteRunOpenFiles,
  useAstGrepRemoteRunRemoveOpenFile,
  useAstGrepRemoteRunReorderOpenFiles,
} from "../_hooks/use-ast-grep-remote-run";
import { useContextTabActions } from "../_hooks/use-context-tab-actions";
import {
  getRealIdFromTabId,
  isIdRemoteRun,
  makeEditorId,
  parseRemoteRunFileId,
  useActiveEditorId,
  useEditorInstanceUpdaters,
} from "../_hooks/use-monaco-editor";
import { useScrollToActiveTab } from "../_hooks/use-scroll-to-active-tab";
import { useStudioStore } from "../_hooks/use-studio-store";
import { FileTab } from "./_sources/file-tab";
import { TestTabsGroup } from "./_sources/test-tabs-group";
import {
  getMonacoLanguage,
  getMonacoPath,
  monacoEditorOptions,
} from "./_utils/monaco-options";

const diffEditorOptions = {
  ...monacoEditorOptions,
  readOnly: true,
  inDiffEditor: true,
  renderSideBySide: false,
  smoothScrolling: false,
  readOnlyMessage: {
    value:
      'To edit, switch from the "View rewritten code" mode to the normal mode.',
  },
} satisfies editor.IDiffEditorConstructionOptions;

interface SourcesTabsProps {
  rewrittenCode: {
    sourceCode: string;
    updatedCode: string;
  };
  editorTheme: string;
}

const basename = (path: string) => {
  return path.split("/").pop() ?? "";
};

export function SourcesTabs({ rewrittenCode, editorTheme }: SourcesTabsProps) {
  const lang = useStudioStore((state) => state.lang);
  const setSource = useStudioStore((state) => state.setSource);
  const addSource = useStudioStore((state) => state.addSource);
  const setActiveRemoteRunFileId = useStudioStore(
    (state) => state.setActiveRemoteRunFileId,
  );
  const activeRemoteRunFileId = useStudioStore(
    (state) => state.activeRemoteRunFileId,
  );
  const setActiveTestSourceId = useStudioStore(
    (state) => state.setActiveTestSourceId,
  );
  const sources = useStudioStore((state) => state.sources);

  const [showDiffMode, setShowDiffMode] = useState(false);
  const [isTestGroupOpen, setIsTestGroupOpen] = useState(true);
  const selectedTabId = useActiveEditorId();
  const remoteRunOpenFiles = useAstGrepRemoteRunOpenFiles();
  const reorderRemoteRunOpenFiles = useAstGrepRemoteRunReorderOpenFiles();
  const remoteFiles = useAstGrepRemoteRunFiles();
  const removeRemoteRunOpenFile = useAstGrepRemoteRunRemoveOpenFile();
  const { viewportRef, registerTabRef } = useScrollToActiveTab(selectedTabId);

  const {
    setContextTab,
    handleCloseThisTab,
    handleCloseOtherTabs,
    handleCloseTabsToRight,
    isCloseThisTabDisabled,
    isCloseOtherTabsDisabled,
    isCloseTabsToRightDisabled,
  } = useContextTabActions();
  const { setEditorInstance, clearEditorInstances } =
    useEditorInstanceUpdaters();

  const debouncedUpdateSource = useCallback(
    (value: string | undefined, id: number) => {
      if (value !== undefined) {
        setSource(value, id);
      }
    },
    [setSource],
  );

  const handleNewTest = useCallback(() => {
    addSource("");
    setIsTestGroupOpen(true);
  }, [addSource]);

  useEffect(() => {
    if (!activeRemoteRunFileId) return;
    const { filePath } = parseRemoteRunFileId(activeRemoteRunFileId);
    if (filePath && !remoteFiles.has(filePath)) {
      setActiveRemoteRunFileId(null);
    }
  }, [activeRemoteRunFileId, remoteFiles, setActiveRemoteRunFileId]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [viewportRef]);

  useEffect(() => {
    return () => {
      clearEditorInstances();
    };
  }, [clearEditorInstances]);

  const codeChanged = useMemo(() => {
    return rewrittenCode.sourceCode !== rewrittenCode.updatedCode;
  }, [rewrittenCode]);

  const handleEditorMount = useCallback(
    (id: string, options: { area?: RemoteRunFileArea | null } = {}) =>
      (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => {
        setEditorInstance(id, editor);

        monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
          noSemanticValidation: true,
        });
        editor.revealLine(0, 0);

        if (options.area) {
          const { startLine, startColumn, endLine, endColumn } = options.area;

          editor.revealLineInCenter(startLine + 1, 0);
          const decorationOptions = {
            range: new monaco.Range(
              startLine + 1,
              startColumn,
              endLine,
              endColumn,
            ),
            options: {
              isWholeLine: true,
              className: "bg-blue-400 opacity-20 transition-opacity",
            },
          };

          const decorations = editor.createDecorationsCollection([
            decorationOptions,
          ]);

          setTimeout(() => {
            decorations.clear();
          }, 3000);
        }
      },
    [setEditorInstance],
  );

  return (
    <>
      <Tabs
        value={selectedTabId}
        onValueChange={(value) => {
          const id = getRealIdFromTabId(value);
          if (!id) return;

          if (isIdRemoteRun(value)) {
            setActiveRemoteRunFileId(id);
          } else {
            setActiveRemoteRunFileId(null);
            setActiveTestSourceId(Number.parseInt(id));
          }
        }}
        className="flex h-[calc(100%-2.5rem)] flex-col"
      >
        <TabsList className="p-0 py-1" variant="underline">
          <div className="relative h-full w-full">
            <ScrollArea
              type="hover"
              ref={viewportRef}
              className="relative h-[calc(100%+1px)] w-full"
            >
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="-mt-px flex h-full w-full">
                    <TestTabsGroup
                      isTestGroupOpen={isTestGroupOpen}
                      setIsTestGroupOpen={setIsTestGroupOpen}
                      registerTabRef={registerTabRef}
                      onContextMenu={(tab) => setContextTab(tab)}
                    />
                    <Button
                      variant="ghost"
                      className="-mt-0.5 mr-1 size-6 cursor-pointer self-center rounded-md p-0"
                      onClick={handleNewTest}
                    >
                      <PlusIcon className="size-4" />
                    </Button>
                    <Reorder.Group
                      values={remoteRunOpenFiles}
                      onReorder={reorderRemoteRunOpenFiles}
                      axis="x"
                      className="flex h-full"
                    >
                      {remoteRunOpenFiles.map((file, index) => {
                        const { filePath } = parseRemoteRunFileId(file);
                        const tabId = makeEditorId(file, "remote_run");

                        return (
                          <Reorder.Item
                            key={file}
                            value={file}
                            className="not-first:-ml-4"
                          >
                            <FileTab
                              key={filePath}
                              value={tabId}
                              ref={(el) => registerTabRef(tabId, el)}
                              onRemove={() => {
                                removeRemoteRunOpenFile(file);
                                setActiveRemoteRunFileId(null);
                              }}
                              onContextMenu={() =>
                                setContextTab({
                                  id: tabId,
                                  index,
                                  type: "remote_run",
                                })
                              }
                              name={basename(filePath)}
                              type="file"
                            />
                          </Reorder.Item>
                        );
                      })}
                    </Reorder.Group>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent className="bg-background/80 w-40 border shadow-2xl backdrop-blur-md">
                  <ContextMenuItem
                    className="h-auto px-2 py-1 text-xs"
                    onClick={handleCloseThisTab}
                    disabled={isCloseThisTabDisabled}
                  >
                    Close
                  </ContextMenuItem>
                  <ContextMenuItem
                    className="h-auto px-2 py-1 text-xs"
                    onClick={handleCloseOtherTabs}
                    disabled={isCloseOtherTabsDisabled}
                  >
                    Close other tabs
                  </ContextMenuItem>
                  <ContextMenuItem
                    className="h-auto px-2 py-1 text-xs"
                    onClick={handleCloseTabsToRight}
                    disabled={isCloseTabsToRightDisabled}
                  >
                    Close tabs to the right
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
              <ScrollBar orientation="horizontal" className="h-px" />
            </ScrollArea>
            <div className="bg-border absolute -bottom-px left-0 z-0 h-px w-full" />
          </div>
          <svg className="hidden" aria-hidden="true" focusable="false">
            <defs>
              <symbol id="tab-geometry-left" viewBox="0 0 214 36">
                <title>Tab geometry left</title>
                <path d="M214 0v36H0v-2c4.5 0 9-3.5 9-8V16C9 8.45 9 4.68 11.34 2.34S17.45 0 25 0h189Z" />
              </symbol>
              <symbol id="tab-geometry-right" viewBox="0 0 214 36">
                <use xlinkHref="#tab-geometry-left" />
              </symbol>
            </defs>
          </svg>
        </TabsList>

        <div className="flex w-full flex-1 flex-col">
          {sources.map((source) => {
            const tabId = makeEditorId(source.id, "source");
            const isActive = selectedTabId === tabId;
            const hasChanged =
              rewrittenCode.sourceCode === source.source &&
              rewrittenCode.updatedCode !== source.source;

            return (
              <TabsContent
                key={source.id}
                value={tabId}
                className="-mt-px h-full p-0 data-[state=active]:block data-[state=inactive]:hidden"
                forceMount
              >
                <div
                  className={cn(
                    "size-full",
                    showDiffMode ? "block" : "hidden",
                    !isActive && "hidden",
                  )}
                >
                  <DiffEditor
                    originalLanguage={getMonacoLanguage(lang)}
                    modifiedLanguage={getMonacoLanguage(lang)}
                    original={source.source}
                    modified={
                      hasChanged ? rewrittenCode.updatedCode : source.source
                    }
                    theme={editorTheme}
                    options={diffEditorOptions}
                  />
                </div>
                <div
                  className={cn(
                    "size-full",
                    showDiffMode ? "hidden" : "block",
                    !isActive && "hidden",
                  )}
                >
                  <Editor
                    className="size-full"
                    language={getMonacoLanguage(lang)}
                    value={source.source}
                    onChange={(value) =>
                      debouncedUpdateSource(value, source.id)
                    }
                    theme={editorTheme}
                    onMount={handleEditorMount(tabId)}
                    options={monacoEditorOptions}
                    loading={<div>Waiting</div>}
                    path={getMonacoPath(lang, source.id.toString())}
                    keepCurrentModel
                  />
                </div>
              </TabsContent>
            );
          })}

          {remoteRunOpenFiles.map((file) => {
            const { filePath, area } = parseRemoteRunFileId(file);
            const tabId = makeEditorId(file, "remote_run");
            const isActive = selectedTabId === tabId;
            const originalSource = remoteFiles.get(filePath)?.source ?? "";
            const hasChanged =
              codeChanged && rewrittenCode.sourceCode === originalSource;
            const readOnlyMessage = {
              value: "Cannot edit remote files",
            };

            return (
              <TabsContent
                key={file}
                value={tabId}
                className="h-full border-none p-0 data-[state=active]:block data-[state=inactive]:hidden"
                forceMount
              >
                <div className={cn("size-full", !isActive && "hidden")}>
                  {hasChanged ? (
                    <DiffEditor
                      originalLanguage={getMonacoLanguage(lang)}
                      modifiedLanguage={getMonacoLanguage(lang)}
                      original={originalSource}
                      modified={rewrittenCode.updatedCode}
                      theme={editorTheme}
                      options={{
                        ...diffEditorOptions,
                        readOnlyMessage,
                      }}
                    />
                  ) : (
                    <Editor
                      className="size-full"
                      language={getMonacoLanguage(lang)}
                      value={originalSource}
                      theme={editorTheme}
                      onMount={handleEditorMount(tabId, { area })}
                      options={{
                        ...monacoEditorOptions,
                        readOnly: true,
                        readOnlyMessage,
                      }}
                      loading={<div>Waiting</div>}
                      path={getMonacoPath(lang, file)}
                      keepCurrentModel
                    />
                  )}
                </div>
              </TabsContent>
            );
          })}
        </div>
      </Tabs>

      <div className="flex h-10 items-center border-t bg-gray-100 px-2 py-2 dark:bg-black">
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <Switch
            id="show-diff-mode"
            checked={showDiffMode}
            onCheckedChange={(checked) => {
              void umami.track("studio:show-diff-mode", {
                mode: checked ? "diff" : "original",
              });
              setShowDiffMode(!!checked);
            }}
          />
          <Label htmlFor="show-diff-mode" className="text-xs">
            <span className="relative">
              View rewritten code
              {codeChanged && !showDiffMode ? (
                <div className="absolute -top-1 -right-1 size-2 animate-pulse rounded-full bg-red-500" />
              ) : null}
            </span>
          </Label>
        </div>
      </div>
    </>
  );
}
